import type { ReactNode } from "react";
import { energyWord, formatHours, formatWeeklySaving } from "../format";
import type { ObservedView, WeekSummaryView } from "../types";
import { TimeTag } from "./CostPreview";

/** "a", "a and b". */
export function sentenceList(items: string[]): string {
  const [first, ...rest] = items;
  if (!first) return "";
  return rest.length ? `${[first, ...rest.slice(0, -1)].join(", ")} and ${rest[rest.length - 1]}` : first;
}

/** Observable outcomes only (19 §4.10, 07). No grades, no verdicts, no hidden deltas. */
export function WeekSummary({ summary, view, children }: {
  summary: WeekSummaryView; view: ObservedView; children?: ReactNode;
}) {
  const total = summary.hoursIn + summary.hoursOn;
  const inPct = total > 0 ? (summary.hoursIn / total) * 100 : 0;
  return (
    <section className="summary" aria-labelledby="summary-title">
      <h2 id="summary-title" className="summary__title">End of week {summary.week}</h2>
      <table className="summary__table">
        <caption className="visually-hidden">Week {summary.week} in figures</caption>
        <tbody>
          <tr><th scope="row">Hours worked this week</th><td className="num">{formatHours(summary.hours)}</td></tr>
          <tr>
            <th scope="row">Where the time went</th>
            <td>
              {/* The bar is visual; the IN and ON hours are written beside it. */}
              <span className="split-bar" aria-hidden="true">
                <span className="split-bar__in" style={{ width: `${inPct}%` }} />
                <span className="split-bar__on" style={{ width: `${100 - inPct}%` }} />
              </span>
              <span className="split-legend">
                <TimeTag kind="in" /> <span className="num">{formatHours(summary.hoursIn)}</span>
                <TimeTag kind="on" /> <span className="num">{formatHours(summary.hoursOn)}</span>
              </span>
            </td>
          </tr>
          <tr><th scope="row">Decisions you made</th><td className="num">{summary.decisions}</td></tr>
          <tr><th scope="row">Issues resolved without you</th><td className="num">{summary.resolvedWithoutYou}</td></tr>
          <tr><th scope="row">Left waiting and settled the usual way</th><td className="num">{summary.handledByYou}</td></tr>
          <tr><th scope="row">Still waiting outside your door</th><td className="num">{view.queue.length}</td></tr>
          <tr><th scope="row">Your energy</th><td>{energyWord(view.hud.en)}</td></tr>
        </tbody>
      </table>
      {summary.energy && (summary.energy.woreDown.length > 0 || summary.energy.keptGoing.length > 0) && (
        <div className="summary__energy">
          <h3>How the week felt</h3>
          {summary.energy.woreDown.length > 0 && <p><strong>What wore you down:</strong> {sentenceList(summary.energy.woreDown)}.</p>}
          {summary.energy.keptGoing.length > 0 && <p><strong>What kept you going:</strong> {sentenceList(summary.energy.keptGoing)}.</p>}
        </div>
      )}
      {summary.hoursBoughtBack.length > 0 && (
        <div className="summary__block">
          <h3>Recurring time no longer coming to you</h3>
          <ul>{summary.hoursBoughtBack.map((h) => <li key={h.label}>{h.label}: {formatWeeklySaving(h.hours)}</li>)}</ul>
        </div>
      )}
      {summary.repeatRequests && summary.repeatRequests.length > 0 && (
        <div className="summary__block">
          <h3>Requests that kept coming back</h3>
          <ul>{summary.repeatRequests.map((r) => <li key={r.label}>{r.label} ×{r.count}</li>)}</ul>
        </div>
      )}
      {children && <div className="summary__actions">{children}</div>}
    </section>
  );
}
